import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import SEOHead from '../components/SEOHead';
import { trackHeroCtaClick } from '../lib/analytics';
import { buildHomeFaqStructuredData } from '../data/homeFaqData';
import { StudioHero } from '../components/home/studio/StudioHero';
import { CertificateClaimToast } from '../components/notifications/CertificateClaimToast';
import { useGetStartedFlow } from '../components/getstarted/GetStartedFlowContext';
import type { FlowIntent } from '../components/home/studio/types';

const HomeBelowFold = lazy(() => import('../components/home/HomeBelowFold'));

export default function Home() {
  const flow = useGetStartedFlow();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [loadBelowFold, setLoadBelowFold] = useState(
    () => typeof navigator !== 'undefined' && !!navigator.webdriver,
  );

  // Load below-fold sections when the user approaches them (or after a long
  // fallback). Avoid idle/LCP timers — those still fire inside Lighthouse's
  // FCP→TTI window and pull framer-motion onto the main thread.
  useEffect(() => {
    if (loadBelowFold) return;
    const node = sentinelRef.current;
    if (!node) return;

    let done = false;
    const load = () => {
      if (done) return;
      done = true;
      observer?.disconnect();
      clearTimeout(fallback);
      setLoadBelowFold(true);
    };

    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) load();
        },
        { rootMargin: '240px 0px' },
      );
      observer.observe(node);
    }

    const fallback = window.setTimeout(load, 10000);

    return () => {
      done = true;
      observer?.disconnect();
      clearTimeout(fallback);
    };
  }, [loadBelowFold]);

  const openFlow = useCallback(
    (intent: FlowIntent) => {
      trackHeroCtaClick(intent === 'agent' ? 'hero_build_agent' : 'hero_create_plan');
      flow.open(intent);
    },
    [flow],
  );

  const faqStructuredData = useMemo(() => buildHomeFaqStructuredData(), []);

  return (
    <div className="home-marketing-page" style={{ background: 'var(--marketing-page-bg)' }}>
      <SEOHead structuredData={faqStructuredData} />
      <CertificateClaimToast />

      <StudioHero onFlow={openFlow} />

      <div ref={sentinelRef} aria-hidden="true" style={{ height: 1, margin: 0, padding: 0 }} />

      {loadBelowFold && (
        <Suspense fallback={null}>
          <HomeBelowFold onFlow={openFlow} />
        </Suspense>
      )}
    </div>
  );
}
